import React, { useEffect, useRef, useState } from "react";
import { Platform } from "react-native";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useDispatch } from "react-redux";
import styled, { useTheme } from "styled-components/native";
import { VideoPlayer } from "expo-video";
import { Container, Spacer } from "@components/atoms";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import Carousel, { ICarouselInstance } from "react-native-reanimated-carousel";
import { OnboardingDataResponse } from "@src/types/onboardingDataResponse";
import { useTypographyStore } from "@redux/slices/typographySlice";
import {
  setOnPreboardingStatus,
  setPostOnboardingCheck,
} from "@redux/slices/appStateSlice";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import NavigationBullets from "@components/molecules/NavigationalBullets";
import { useOnSnapped } from "./utils/onSnapped";
import { CarouselItem } from "./utils/CarouselItem";
import { BottomButtons } from "./utils/BottomButtons";
import { OnboardingScreenProps } from "./types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useUpdateUser } from "@apis/user";
import { css } from "styled-components/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const OnboardingScreen: React.FC<OnboardingScreenProps> = ({
  onboardingData,
  isPostOnboarding,
}) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const [onboardingDataState, setOnboardingDataState] = useState<
    OnboardingDataResponse[]
  >([]);
  const [activeNavigationIndex, setActiveNavigationIndex] = useState(0);
  const carrouselRef = useRef<ICarouselInstance>(null);
  const videoPlayers = useRef<(VideoPlayer | null)[]>([]);
  const navigation = useNavigation<NavigationProp<any>>();
  const orgSettings = useOrgSettingsSlice();
  const dispatch = useDispatch();
  const [width, setWidth] = useState(0);
  const [loadedVideos, setLoadedVideos] = useState<number[]>([]);
  const userInfo = useUserInfo();
  const updateUser = useUpdateUser(userInfo.getUserId());
  const insets = useSafeAreaInsets();

  const { onSnapped, clearCurrentTimeout } = useOnSnapped(
    carrouselRef,
    videoPlayers,
    setActiveNavigationIndex
  );

  const goToPreviousScreen = () => {
    if (activeNavigationIndex > 0) {
      carrouselRef.current?.scrollTo({
        index: activeNavigationIndex - 1,
        animated: true,
      });
    }
  };

  const goToNextScreen = () => {
    clearCurrentTimeout();
    if (activeNavigationIndex < onboardingDataState.length - 1) {
      carrouselRef.current?.scrollTo({
        index: activeNavigationIndex + 1,
        animated: true,
      });
    }
  };

  const skipToAppNavigator = () => {
    dispatch(setPostOnboardingCheck(false));
    videoPlayers.current[activeNavigationIndex]?.pause();
    navigation.goBack();
  };

  const goToAppNavigator = () => {
    updateUser.mutate(
      {
        user: {
          ...(theme.isLargeScreen
            ? { onboarding_status_on_web: true }
            : theme.isMobile
            ? { onboarding_status: true }
            : { onboarding_status_on_ipad: true }),
        },
      },
      {
        onSuccess() {
          videoPlayers.current[activeNavigationIndex]?.pause();
          navigation.goBack();
        },
      }
    );
  };

  const toLoginScreen = () => {
    videoPlayers.current[activeNavigationIndex]?.pause();
    dispatch(setOnPreboardingStatus(true));
    navigate(Routes.Authentication);
  };

  const toRegisterScreen = () => {
    videoPlayers.current[activeNavigationIndex]?.pause();
    dispatch(setOnPreboardingStatus(true));
    navigate(Routes.Register);
  };

  const handleVideoLoad = (index: number) => {
    setLoadedVideos((old: any) => [...old, index]);
  };

  useEffect(() => {
    if (Array.isArray(onboardingData) && onboardingData.length) {
      const data = JSON.parse(JSON.stringify(onboardingData));
      if (data.length > 1) {
        data.sort((a: any, b: any) => a.sequence - b.sequence);
      }
      setOnboardingDataState(data);
    }
  }, [onboardingData, loadedVideos]);

  const changeSelectedIndex = (i: any) => {
    carrouselRef.current?.scrollTo({
      index: i,
      animated: true,
    });
  };

  useEffect(() => {
    if (
      onboardingDataState.length > 0 &&
      (Platform.OS === "web" ||
        videoPlayers.current[activeNavigationIndex] === null ||
        (videoPlayers.current[activeNavigationIndex] &&
          loadedVideos.includes(activeNavigationIndex)))
    ) {
      const slideTime = onboardingDataState[activeNavigationIndex].slide_time;
      onSnapped(
        activeNavigationIndex,
        Number(slideTime) ||
          Number(orgSettings?.initStatus?.onboarding_screen_time) ||
          5000,
        onboardingDataState
      );
    }
  }, [activeNavigationIndex, loadedVideos, onboardingDataState]);

  return (
    <MainContainer>
      <CenterContainer isPostOnboarding={isPostOnboarding}>
        <ContentContainer
          isPostOnboarding={isPostOnboarding}
          onLayout={(event: any) => {
            setWidth(event.nativeEvent.layout.width);
          }}
        >
          {width > 0 && (
            <ResponsiveCarouselWrapper theme={theme}>
              <Carousel
                enabled={onboardingDataState.length > 1}
                ref={carrouselRef}
                data={onboardingDataState}
                renderItem={({
                  item,
                  index,
                }: {
                  item: OnboardingDataResponse;
                  index: number;
                }) => {
                  if (activeNavigationIndex === index) {
                    return (
                      <CarouselItem
                        mediaItem={item}
                        index={index}
                        videoPlayers={videoPlayers}
                        typography={typography}
                        theme={theme}
                        isPostOnboarding={isPostOnboarding}
                        onVideoLoad={handleVideoLoad}
                      />
                    );
                  }
                  return <></>;
                }}
                onScrollEnd={(index) => {
                  clearCurrentTimeout();
                  setActiveNavigationIndex(index);
                }}
                width={width}
                autoPlay={false}
                loop={false}
              />
            </ResponsiveCarouselWrapper>
          )}
          <BottomContainer insets={insets}>
            {onboardingDataState.length > 1 && (
              <NavigationBullets
                selected={activeNavigationIndex}
                size={onboardingDataState.length}
                changeSelectedIndex={changeSelectedIndex}
                bulletWidth={
                  theme.isTablet || theme.isLargeScreen ? "13px" : "8px"
                }
              />
            )}
            {(theme.isMobile || isPostOnboarding) && (
              <Spacer size={SpaceType.Double} />
            )}
            {(theme.isMobile || isPostOnboarding) && (
              <Container>
                <BottomButtons
                  activeNavigationIndex={activeNavigationIndex}
                  onboardingDataState={onboardingDataState}
                  toRegisterScreen={toRegisterScreen}
                  toLoginScreen={toLoginScreen}
                  buttonCategory={ButtonCategory}
                  buttonType={ButtonType}
                  isPostOnboarding={isPostOnboarding}
                  skipToAppNavigator={skipToAppNavigator}
                  goToAppNavigator={goToAppNavigator}
                  isButtonLoading={updateUser.isLoading}
                  isButtonDisabled={updateUser.isLoading}
                  goToPreviousScreen={goToPreviousScreen}
                  goToNextScreen={goToNextScreen}
                />
              </Container>
            )}
            {isPostOnboarding && <Spacer size={SpaceType.Double} />}
          </BottomContainer>
        </ContentContainer>
      </CenterContainer>
    </MainContainer>
  );
};

const ResponsiveCarouselWrapper: React.FC<{
  theme: any;
  children: React.ReactNode;
}> = ({ theme, children }) => {
  if (theme.isLargeScreen) {
    return <CarouselContainer>{children}</CarouselContainer>;
  }
  return <>{children}</>; // just render children directly
};

const MainContainer = styled.SafeAreaView`
  flex: 1;
  background-color: ${({ theme }: { theme: any }) => theme["core-01"]};
`;

const ContentContainer = styled.View<{ isPostOnboarding?: boolean }>`
  flex: 1;
  background-color: ${({ theme }: { theme: any }) => theme["core-01"]};
  max-width: 608px;
  width: 100%;
`;

const CenterContainer = styled.View<{ isPostOnboarding?: boolean }>`
  align-items: center;
  flex: 1;
`;

const BottomContainer = styled.View<{ insets: { bottom: number } }>`
  ${({ theme }: { theme: any }) =>
    !theme.isLargeScreen &&
    css`
      position: absolute;
    `}
  bottom: ${({ theme, insets }: { theme: any; insets: { bottom: number } }) =>
    Platform.OS === "android"
      ? theme.isTablet
        ? 0
        : insets.bottom > 0
        ? insets.bottom + 5
        : 40
      : theme.isMobile
      ? 10
      : 0}px;
  left: 0;
  right: 0;
`;

const CarouselContainer = styled.View`
  flex: 1;
`;

export default OnboardingScreen;
